import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UserProfile, UserPastPurchase } from 'models/user.model'

import APILoadingStatus from 'types/api-loading-status'
import {
  changeUserPassword,
  getUserProfile,
  getUserTransactionsByLocation,
  getUserTransactions,
  getUserBalance,
  getUserAppointments,
  resendReceipt,
  setUserFamily,
  removeUserFamily,
  createChild,
  getChildren,
  editChild,
  getFamilyMemberAppointments,
  updateUserProfile,
  getWaiverStatusForUser,
  postWaiverStatusForUser,
} from './users.actions'
import asyncReducers from '../../../utils/async-reducers'
import {
  UserBalanceResponse,
  UserTransactionsByLocationResponse,
} from 'services/user/user.types'
import { Appointment } from 'services/appointment'
import toast from 'react-hot-toast'
import { WaiverStatusResponse } from 'services/waiver'

// Helper to safely handle error messages that can be string or boolean
const getErrorMessage = (error: string | boolean): string =>
  typeof error === 'string' ? error : 'An error occurred'

export type UserState = {
  loading: APILoadingStatus
  loadingUserTransaction: APILoadingStatus
  userProfile: UserProfile | null
  userTransactions: UserPastPurchase[] | null
  userTransactionsTotal: number
  userBalance: number | null
  userAppointments: Appointment[]
  waiverSigned: boolean
  waiverUrl: string | undefined
}

const initialState: UserState = {
  loading: APILoadingStatus.Idle,
  loadingUserTransaction: APILoadingStatus.Idle,
  userProfile: null,
  userTransactions: null,
  userTransactionsTotal: 0,
  userBalance: null,
  userAppointments: [],
  waiverUrl: undefined,
  waiverSigned: false,
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserTransactions(state) {
      state.userTransactions = null
      state.userTransactionsTotal = 0
      state.userBalance = null
      state.userAppointments = []
    },
  },
  extraReducers: builder => {
    asyncReducers(
      builder,
      setUserFamily,
      'loading',
      (state, action: PayloadAction<UserProfile | undefined>) => {
        if (action.payload && action.payload) {
          state.userProfile = action.payload
          toast.success('Relation registered successfully')
        }
      }
    )
    asyncReducers(builder, createChild, 'loading', (_state, action) => {
      // The component refetches the parent profile on success so the new
      // child appears in the list; errors are toasted in the service layer.
      if (action.payload && !action.payload.error) {
        toast.success('Child added')
      }
    })
    asyncReducers(
      builder,
      removeUserFamily,
      'loading',
      (state, action: PayloadAction<UserProfile | undefined>) => {
        if (action.payload) {
          state.userProfile = action.payload
          toast.success('Relation removed')
        }
      }
    )
    asyncReducers(
      builder,
      getUserAppointments,
      'loading',
      (state, action: PayloadAction<Appointment[]>) => {
        if (action.payload && action.payload.length > 0) {
          state.userAppointments = action.payload
        }
      }
    )
    asyncReducers(
      builder,
      getUserProfile,
      'loading',
      (state, action: PayloadAction<UserProfile>) => {
        if (action.payload && !action.payload.error) {
          state.userProfile = action.payload
        }
      }
    )
    asyncReducers(
      builder,
      updateUserProfile,
      'loading',
      (state, action) => {
        if (action.payload && !action.payload.error && action.payload.data) {
          state.userProfile = action.payload.data
          toast.success('Profile updated')
        }
      }
    )
    asyncReducers(builder, changeUserPassword, 'loading', (_state, action) => {
      if (action.payload && !action.payload.error) {
        toast.success('Password updated')
      }
    })
    asyncReducers(
      builder,
      getUserTransactionsByLocation,
      'loadingUserTransaction',
      (state, action: PayloadAction<UserTransactionsByLocationResponse>) => {
        if (action.payload && !action.payload.error) {
          state.userTransactions = action.payload.data
        }
      }
    )
    asyncReducers(
      builder,
      getUserTransactions,
      'loadingUserTransaction',
      (
        state,
        action: PayloadAction<{
          data: UserPastPurchase[]
          totalCount: number
        }>
      ) => {
        if (action.payload) {
          state.userTransactions = action.payload.data
          state.userTransactionsTotal = action.payload.totalCount
        }
      }
    )
    asyncReducers(builder, resendReceipt, 'loading', (_state, action) => {
      if (action.payload && !action.payload.error) {
        toast.success('Receipt sent. Check your email.')
      }
    })
    asyncReducers(
      builder,
      getUserBalance,
      'loading',
      (state, action: PayloadAction<UserBalanceResponse>) => {
        if (action.payload && !action.payload.error) {
          state.userBalance = action.payload.data.balance
        }
      }
    )
    asyncReducers(
      builder,
      getWaiverStatusForUser,
      'loading',
      (state, action) => {
        if (action.payload && !action.payload.error) {
          const { waiver_signed, waiver_url } = action.payload.data
          ;(state.waiverSigned = waiver_signed),
            (state.waiverUrl = waiver_url ?? undefined)
        }
      }
    ),
      asyncReducers(
        builder,
        postWaiverStatusForUser,
        'loading',
        (state, action: any) => {
          if (action.payload.data) {
            const { waiver_signed, waiver_url } = action.payload.data
            ;(state.waiverSigned = waiver_signed),
              (state.waiverUrl = waiver_url ?? undefined)
          }
          if (action.payload.error) {
            toast.error(getErrorMessage(action.payload.error))
          }
        }
      )
  },
})

export default userSlice.reducer
export const userActions = {
  ...userSlice.actions,
  ...userSlice.reducer,
  changeUserPassword,
  getUserProfile,
  getUserBalance,
  getUserTransactionsByLocation,
  getUserTransactions,
  getUserAppointments,
  resendReceipt,
  setUserFamily,
  removeUserFamily,
  createChild,
  getChildren,
  editChild,
  getFamilyMemberAppointments,
  updateUserProfile,
  getWaiverStatusForUser,
  postWaiverStatusForUser,
}
